/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/init-declarations */
/**
 * 通用确认对话框 Hook（已修复 useMemo deps 警告）
 */
import {App, Checkbox} from 'antd';
import {useState, useCallback, useRef} from 'react';
import './index.module.less';

export interface ConfirmModalConfig {
    /** 对话框标题 */
    title: React.ReactNode;
    /** 对话框内容 */
    content: React.ReactNode;
    closable?: boolean;
    /** 确认按钮文字 */
    okText?: string;
    /** 是否为危险操作（红色按钮） */
    danger?: boolean;
    /** 对话框宽度 */
    width?: number;
    /** 成功提示文字 */
    successMessage?: string;
    /** 底部左侧自定义扩展组件 */
    footerExtra?: React.ReactNode;
    /** 是否显示"不再提示"勾选框 */
    showDontAskAgain?: boolean;
    /** "不再提示"的默认值 */
    dontAskAgainDefault?: boolean;
    /** "不再提示"勾选后的回调 */
    onDontAskAgainChange?: (checked: boolean) => void;
    /** 自定义类名 */
    className?: string;
}

/**
 * 通用确认对话框 Hook
 * 用于处理需要二次确认的异步操作
 */

export function useConfirmModal() {
    const {modal: rawModal, message: rawMessage} = App.useApp();

    /** 使用 ref 保证 modal / message 稳定 */
    const modalRef = useRef(rawModal);
    const messageRef = useRef(rawMessage);
    modalRef.current = rawModal;
    messageRef.current = rawMessage;

    const [confirmLoading, setConfirmLoading] = useState(false);


    /**
     * 显示确认对话框
     * @param config - 对话框配置
     * @param onConfirm - 确认后执行的异步操作
     */
    const showConfirm = useCallback(
        (config: ConfirmModalConfig, onConfirm: () => Promise<void>) => {
            let modalInstance: ReturnType<typeof modalRef.current.confirm>;
            let dontAskAgainChecked = config.dontAskAgainDefault ?? false;

            const updateModal = (loading: boolean) => {
                modalInstance?.update({
                    okButtonProps: {
                        loading,
                        danger: config.danger ?? false
                    }
                });
            };

            // 构建底部左侧扩展组件
            const footerRender = (_: any, {OkBtn, CancelBtn}: any) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <div>
                        {config.footerExtra ? config.footerExtra : null}
                        {config.showDontAskAgain ? (
                            <Checkbox
                                defaultChecked={config.dontAskAgainDefault}
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={e => {
                                    dontAskAgainChecked = e.target.checked;
                                    config.onDontAskAgainChange?.(e.target.checked);
                                }}
                            >
                                不再提示
                            </Checkbox>
                        ) : null}
                    </div>
                    <div>
                        <CancelBtn />
                        <OkBtn />
                    </div>
                </div>
            );

            // ✅ 确保配置对象结构始终一致，所有属性都显式定义
            modalInstance = modalRef.current.confirm({
                title: config.title,
                content: config.content,
                okText: config.okText ?? '确定',
                cancelText: '取消',
                okButtonProps: {
                    danger: config.danger ?? false,
                    loading: false
                },
                width: config.width ?? 400,
                centered: true,
                wrapClassName: `custom-modal-wrapper ${config.className ?? ''}`.trim(),
                closable: config.closable ?? false,
                icon: null,
                footer: footerRender,
                async onOk() {
                    try {
                        updateModal(true);
                        setConfirmLoading(true);
                        await onConfirm();

                        if (config.successMessage) {
                            messageRef.current.success(config.successMessage);
                        }

                        setConfirmLoading(false);
                        updateModal(false);
                    } catch (error) {
                        setConfirmLoading(false);
                        updateModal(false);
                        return Promise.reject(); // 阻止 Modal 关闭
                    }
                },
                onCancel() {
                    // 保持空函数，确保结构一致
                }
            });
        },
        [] // ✅ 移除 confirmLoading 依赖，避免不必要的重新创建
    );

    return {showConfirm, confirmLoading};
}
